<script setup lang="ts">
/**
 * 시스템 — 문서 서식 관리.
 *
 * 설계: docs/designs/core-document-template-engine.md §7.6.5,
 *       docs/designs/mes-document-template-management.md §5(개선)
 * 문서 종류는 공급자 registry 가 SSOT 인 고정 소수 목록이라 필터 콤보박스 대신
 * **종류별 섹션으로 전부 펼쳐** 보여준다 — 서식이 없는 종류도 "프리셋 사용 중"으로
 * 항상 보이고, 섹션마다 [새 서식]이 그 종류로 바로 시작한다. 편집은 100% 폭 드로어.
 */
import { ElMessage, ElMessageBox } from 'element-plus';
import { computed, onMounted, ref } from 'vue';
import type { DocumentTypeCatalog } from '@axsolutionsdev/shared';

import AxDocumentTemplateDesigner from '../../components/document/AxDocumentTemplateDesigner.vue';
import { useDocumentTemplateDraft } from '../../components/document/designer/use-document-template-draft';
import { confirmEditClose } from '../../feedback/confirm-dialog';
import {
  deleteDocumentTemplate,
  fetchDocumentPresetLayout,
  fetchDocumentTypes,
  listDocumentTemplates,
  setDefaultDocumentTemplate,
  updateDocumentTemplate,
  type DocumentTemplateRow,
} from '../../api/documents';

const rows = ref<DocumentTemplateRow[]>([]);
const docTypes = ref<DocumentTypeCatalog[]>([]);
const loading = ref(false);
const includeHistory = ref(false);
const designerVisible = ref(false);
const busy = ref(false);

const draft = useDocumentTemplateDraft();

/** 종류별 섹션 — registry 의 모든 문서 종류를 서식 유무와 무관하게 노출한다. */
const sections = computed(() =>
  docTypes.value.map((t) => ({
    ...t,
    templates: rows.value.filter((r) => r.docType === t.docType),
  })),
);

async function loadTypes(): Promise<void> {
  try {
    docTypes.value = await fetchDocumentTypes();
  } catch {
    docTypes.value = [];
  }
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    rows.value = await listDocumentTemplates({
      includeInactive: true,
      includeHistory: includeHistory.value,
    });
  } catch (e) {
    ElMessage.error(e instanceof Error ? e.message : '서식 목록을 불러오지 못했습니다.');
  } finally {
    loading.value = false;
  }
}

function openEdit(row: DocumentTemplateRow): void {
  draft.openEdit(row);
  designerVisible.value = true;
}

/** 신규 — 섹션의 문서 종류로 시작, 공급자 기본 서식(프리셋)에서 시작할지 선택한다. */
async function openCreate(docType: string): Promise<void> {
  let preset = null;
  try {
    const answer = await ElMessageBox.confirm(
      '기본 제공 서식(프리셋)에서 시작하면 표준 양식을 그대로 두고 고칠 수 있습니다.',
      '새 서식',
      {
        confirmButtonText: '프리셋으로 시작',
        cancelButtonText: '빈 서식',
        distinguishCancelAndClose: true,
        type: 'info',
      },
    ).then(() => 'preset' as const);
    if (answer === 'preset') preset = await fetchDocumentPresetLayout(docType);
  } catch (action) {
    if (action === 'close') return; // X 는 취소
  }
  draft.openCreate(docType, preset);
  draft.markDirty();
  designerVisible.value = true;
}

/** 차단 오류는 즉시 막고, 경고는 사용자가 확인 후 통과할 수 있게 한다. */
async function confirmDraftIssues(actionLabel: string): Promise<boolean> {
  const { blocking, warnings } = draft.validateDraft();
  if (blocking.length) {
    ElMessage.warning(blocking[0]);
    return false;
  }
  if (warnings.length) {
    try {
      await ElMessageBox.confirm(warnings.join('\n'), `이대로 ${actionLabel}할까요?`, {
        confirmButtonText: actionLabel,
        cancelButtonText: '계속 편집',
        type: 'warning',
      });
    } catch {
      return false;
    }
  }
  return true;
}

async function handleSave(): Promise<void> {
  if (!(await confirmDraftIssues('저장'))) return;
  const saved = await draft.saveDraft();
  if (saved) await load();
}

async function handleRevise(): Promise<void> {
  if (!(await confirmDraftIssues('개정 발행'))) return;
  try {
    await ElMessageBox.confirm(
      `현재 버전(v${draft.draftVersion.value})은 이력으로 보존되고 v${draft.draftVersion.value + 1} 이 발행됩니다.`,
      '개정 발행',
      { confirmButtonText: '개정 발행', cancelButtonText: '취소', type: 'warning' },
    );
  } catch {
    return;
  }
  const saved = await draft.saveAsRevision();
  if (saved) await load();
}

async function handleSetDefault(row: DocumentTemplateRow): Promise<void> {
  busy.value = true;
  try {
    await setDefaultDocumentTemplate(row.id);
    ElMessage.success(`「${row.name}」 을 기본 서식으로 지정했습니다.`);
    await load();
  } catch (e) {
    ElMessage.error(e instanceof Error ? e.message : '기본 지정 실패');
  } finally {
    busy.value = false;
  }
}

async function handleToggleActive(row: DocumentTemplateRow): Promise<void> {
  busy.value = true;
  try {
    await updateDocumentTemplate(row.id, { isActive: !row.isActive });
    await load();
  } catch (e) {
    ElMessage.error(e instanceof Error ? e.message : '상태 변경 실패');
  } finally {
    busy.value = false;
  }
}

async function handleDelete(row: DocumentTemplateRow): Promise<void> {
  try {
    await ElMessageBox.confirm(`「${row.name}」 서식을 삭제할까요?`, '삭제', {
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      type: 'warning',
    });
  } catch {
    return;
  }
  try {
    await deleteDocumentTemplate(row.id);
    ElMessage.success('삭제되었습니다.');
    await load();
  } catch (e) {
    ElMessage.error(e instanceof Error ? e.message : '삭제 실패');
  }
}

async function attemptClose(): Promise<void> {
  if (!draft.hasChanges.value) {
    designerVisible.value = false;
    return;
  }
  const choice = await confirmEditClose();
  if (choice === 'abort') return;
  if (choice === 'save') {
    await handleSave();
    if (draft.hasChanges.value) return; // 저장 실패 시 드로어 유지
  }
  designerVisible.value = false;
}

onMounted(() => {
  void loadTypes().then(load);
});
</script>

<template>
  <section class="ax-page system-doc-templates">
    <header class="system-doc-templates__head">
      <h3 class="system-doc-templates__title">문서 서식 관리</h3>
      <div class="system-doc-templates__head-actions">
        <el-checkbox v-model="includeHistory" size="small" @change="load"
          >개정 이력 포함</el-checkbox
        >
        <el-button size="small" :loading="loading" @click="load">새로고침</el-button>
      </div>
    </header>

    <el-empty
      v-if="!loading && sections.length === 0"
      description="등록된 문서 종류가 없습니다."
      :image-size="80"
    />

    <div v-loading="loading" class="system-doc-templates__sections">
      <section v-for="s in sections" :key="s.docType" class="doc-type-section">
        <header class="doc-type-section__head">
          <span class="doc-type-section__label">{{ s.label }}</span>
          <span class="doc-type-section__count">
            {{
              s.templates.length ? `서식 ${s.templates.length}개` : '기본 제공 양식(프리셋) 사용 중'
            }}
          </span>
          <el-button
            v-permission="'system:document-template:write'"
            size="small"
            type="primary"
            text
            @click="openCreate(s.docType)"
          >
            + 새 서식
          </el-button>
        </header>

        <table v-if="s.templates.length" class="doc-type-section__table">
          <thead>
            <tr>
              <th class="col-code">코드</th>
              <th>서식명</th>
              <th class="col-ver">버전</th>
              <th class="col-tag">기본</th>
              <th class="col-tag">사용</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in s.templates" :key="row.id" :class="{ 'is-inactive': !row.isActive }">
              <td class="col-code">{{ row.code }}</td>
              <td>{{ row.name }}</td>
              <td class="col-ver">v{{ row.version }}</td>
              <td class="col-tag">
                <el-tag v-if="row.isDefault" size="small" type="success">기본</el-tag>
              </td>
              <td class="col-tag">
                <el-tag size="small" :type="row.isActive ? 'success' : 'info'">
                  {{ row.isActive ? '사용' : '중지' }}
                </el-tag>
              </td>
              <td class="col-actions">
                <el-button size="small" type="primary" link @click="openEdit(row)">편집</el-button>
                <el-button
                  v-if="!row.isDefault && row.isActive"
                  v-permission="'system:document-template:write'"
                  size="small"
                  link
                  :disabled="busy"
                  @click="handleSetDefault(row)"
                >
                  기본 지정
                </el-button>
                <el-button
                  v-permission="'system:document-template:write'"
                  size="small"
                  link
                  :disabled="busy"
                  @click="handleToggleActive(row)"
                >
                  {{ row.isActive ? '중지' : '사용' }}
                </el-button>
                <el-button
                  v-permission="'system:document-template:write'"
                  size="small"
                  type="danger"
                  link
                  @click="handleDelete(row)"
                >
                  삭제
                </el-button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="doc-type-section__empty">
          등록된 서식이 없습니다 — [+ 새 서식]으로 이 문서의 종이 모양을 직접 만들 수 있습니다.
        </p>
      </section>
    </div>

    <el-drawer
      v-model="designerVisible"
      :title="
        draft.draftId.value
          ? `서식 편집 — ${draft.meta.name} (v${draft.draftVersion.value})`
          : '새 서식'
      "
      size="100%"
      destroy-on-close
      class="system-doc-templates__drawer"
      :before-close="attemptClose"
    >
      <AxDocumentTemplateDesigner :draft="draft" :doc-types="docTypes" />
      <template #footer>
        <div class="system-doc-templates__footer">
          <span v-if="draft.hasChanges.value" class="system-doc-templates__dirty"
            >● 저장되지 않은 변경</span
          >
          <el-button @click="attemptClose">닫기</el-button>
          <el-button
            v-if="draft.draftId.value"
            v-permission="'system:document-template:write'"
            type="warning"
            :loading="draft.saving.value"
            @click="handleRevise"
          >
            개정 발행 (v{{ draft.draftVersion.value + 1 }})
          </el-button>
          <el-button
            v-permission="'system:document-template:write'"
            type="primary"
            :loading="draft.saving.value"
            @click="handleSave"
          >
            저장
          </el-button>
        </div>
      </template>
    </el-drawer>
  </section>
</template>

<style scoped>
.system-doc-templates {
  height: 100%;
  min-height: 0;
  overflow: auto;
}

.system-doc-templates__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.system-doc-templates__title {
  margin: 0;
  font-size: 16px;
}

.system-doc-templates__head-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.system-doc-templates__sections {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.doc-type-section {
  border: 1px solid var(--el-border-color-light);
  border-radius: 8px;
  background: var(--el-bg-color);
}

.doc-type-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.doc-type-section__label {
  font-weight: 600;
}

.doc-type-section__count {
  margin-right: auto;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.doc-type-section__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.doc-type-section__table th,
.doc-type-section__table td {
  padding: 8px 14px;
  text-align: left;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.doc-type-section__table thead th {
  font-weight: 500;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  background: var(--el-fill-color-lighter);
}

.doc-type-section__table tbody tr:last-child td {
  border-bottom: none;
}

.doc-type-section__table tr.is-inactive td {
  color: var(--el-text-color-placeholder);
}

.col-code {
  width: 110px;
}

.col-ver {
  width: 70px;
}

.col-tag {
  width: 80px;
}

.col-actions {
  width: 240px;
  text-align: right;
  white-space: nowrap;
}

.doc-type-section__empty {
  margin: 0;
  padding: 14px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.system-doc-templates__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.system-doc-templates__dirty {
  margin-right: auto;
  font-size: 12px;
  color: var(--el-color-warning);
}
</style>

<style>
/* 드로어 본문이 3분할 디자이너의 높이 기준이 되도록 (scoped 는 el-drawer 내부에 안 닿음) */
.system-doc-templates__drawer .el-drawer__body {
  padding: 12px;
  overflow: hidden;
}
</style>
